//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR imports

import { IIMRPopup, IMRPopupOptions } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { IMRBaseControlController } from '../../../common/base'
import { TypeHelper, LocalizationHelper, ConvertHelper, LogHelper } from '../../../../helpers'

import { Enums } from '../../../../models/common'
import { OpFile } from '../../../../models/core'


import { UserSessionService } from '../../../../services/user-session'

import { CreateEditMessageOptions, ICreateEditMessage } from './create-edit-supplier-message.interfaces'
import { SupplierService } from '../../../../services/supplier'
import { SystemInternationalizationService } from '../../../../services/system'
import { Language } from '../../../../services/system/system.service.interfaces'


export class CreateEditSupplierMessageController extends
    IMRBaseControlController<ICreateEditMessage, CreateEditMessageOptions>
    implements
    ICreateEditMessage {

    //#region Consts

    private static get LOG_STRING(): string { return '[SIMAX.Supllier.create-edit-message.Controller] - %s' }

    //#endregion

    //#region Members

    public languageSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    public messageHtmlEditorOptions: DevExpress.ui.dxTextAreaOptions
    public popupOptions: IMRPopupOptions
    public impossibleCreateMessage: string = 'impossible create new template'
    public isDisabled: boolean

    private isNewMessage: boolean
    private popup: IIMRPopup
    private submitButton: DevExpress.ui.dxButton
    private langSelectBox: DevExpress.ui.dxSelectBox
    private messageBox: DevExpress.ui.dxTextArea

    //#endregion 


    //#region Ctor

    constructor(
        private userSession: UserSessionService,
        private supplierService: SupplierService,
        private systemIInternacinalizingService: SystemInternationalizationService
    ) {
        super()
    }

    //#endregion

    //#region ICreateEditMessage Implementation

    public show(): void {
        if (this.popup != null) {
            this.popup.show()
        }
    }

    public hide(): void {
        if (this.popup != null) {
            this.popup.hide()
        }
    }

    public showLoading(): void {
        if (this.popup != null) {
            this.popup.showLoading()
        }
    }

    public hideLoading(): void {
        if (this.popup != null) {
            this.popup.hideLoading()
        }
    }

    public updateFileSource(file: OpFile): void {
        this.isNewMessage = file == null
        const languages: Language[] = this.getAvailablelanguages()
        const selectedlanguage: Language = languages.find((lang: Language) => lang.id == this.options.idLanguage)

        this.popup.getInnerPopup().option({ title: this.isNewMessage ? LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AddTemplate) : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditTemplate) })

        if (this.isNewMessage && _.size(languages) === 0) {
            this.isDisabled = true
        } else {
            this.isDisabled = false
            if (this.langSelectBox != null) {

                this.langSelectBox.option({ items: languages, value: selectedlanguage != null ? this.options.idLanguage : languages[0].id, readOnly: false })
            } else {
                this.languageSelectBoxOptions.items = languages
                this.languageSelectBoxOptions.value = selectedlanguage != null ? this.options.idLanguage : languages[0].id
                this.languageSelectBoxOptions.readOnly = false
            }

            if (file != null && file.idFile > 0) {
                this.popup.showLoading()

                this.supplierService
                    .getMessageFileContent(file.idFile)
                    .then((opFile: OpFile) => {
                        if (opFile != null) {
                            let fileData: number[] = null

                            try {
                                if (typeof (<number[]>opFile.fileBytes).map !== 'function') {
                                    fileData = ConvertHelper.base64ToByteArray(opFile.fileBytes as string)
                                }
                                else {
                                    fileData = <number[]>opFile.fileBytes
                                }

                                const message: string = ConvertHelper.byteArrayToUnicodeString([].slice.apply(fileData))

                                if (this.messageBox != null) {
                                    this.messageBox.option({ value: message })
                                }

                                const selectedlanguage: Language = languages.find((lang: Language) => lang.id == this.options.idLanguage)

                                this.langSelectBox.option({ items: languages, value: this.options.idLanguage, readOnly: true })

                            } catch (e) {
                                LogHelper.error(CreateEditSupplierMessageController.LOG_STRING, "Unable to parse message bytes. Resetting...")
                            }
                        } else {
                            this.langSelectBox.option({ value: this.userSession.getDataProvider().System.getCurrentLanguage() })
                        }
                    })
                    .always(() => {
                        this.popup.hideLoading()
                    })
            }
        }
    }

    //#endregion


    //#region Component

    protected initialize(): void {
        this.isNewMessage = this.options.messageFile == null

        this.initializeLanguagesSelectBox()
        this.setMessageHtmlEditorOptions()
        this.initializePopup()
    }

    private resetValues(): void {
        this.options.idLanguage = null
        this.options.messageFile = null
        this.langSelectBox.reset()
        this.messageBox.reset()
    }

    //#endregion


    //#region Content

    private initializePopup(): JQueryPromise<void> {
        this.popupOptions = {
            bottomToolbarOptions: {
                innerOptions: {
                    items: [{
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            disabled: true,
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),

                            onClick: () => {
                                this.submitMessage()
                            },

                            onInitialized: (e: any) => {
                                this.submitButton = e.component
                            }
                        }
                    }, {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            disabled: false,
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Close),

                            onClick: () => {
                                this.popup.hide()
                            }
                        }
                    },],
                }
            },

            innerOptions: {
                title: this.isNewMessage ? LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AddTemplate) : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EditTemplate),

                onHidden: () => { this.resetValues() },
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.popup = e.component
            }
        }

        return TypeHelper.onInitializedAll(this.popupOptions)
    }

    private initializeLanguagesSelectBox(): JQueryPromise<void> {
        const languages: Language[] = this.getAvailablelanguages()
        const selectedlanguage: Language = languages.find((lang: Language) => lang.id == this.userSession.getDataProvider().System.getCurrentLanguageId())

        this.languageSelectBoxOptions = {
            displayExpr: 'title',
            valueExpr: 'id',
            items: this.getAvailablelanguages(),
            value: selectedlanguage != null ? this.userSession.getDataProvider().System.getCurrentLanguageId() : ((_.size(languages) > 0) ? languages[0].id : null),
            onInitialized: (e: any) => {
                this.langSelectBox = e.component
            },
            onValueChanged: (e: any) => {
                if (this.messageBox != null && this.submitButton != null) {
                    this.submitButton.option({ disabled: TypeHelper.isNullOrEmpty(this.messageBox.option('value')) })
                }
            }
        }

        return TypeHelper.onInitializedAll(this.languageSelectBoxOptions)
    }

    private setMessageHtmlEditorOptions(): JQueryPromise<void> {
        this.messageHtmlEditorOptions = {
            height: '100%',

            onOptionChanged: (e: any) => {
                if (e.name == 'text' && this.submitButton != null) {
                    this.submitButton.option({ disabled: (!e.value || e.value == undefined || e.value == "" || e.value.length == 0 || e.value === e.previousValue) })
                }
            },
            onInitialized: (e: any) => {
                this.messageBox = e.component
            }
        }

        return TypeHelper.onInitializedAll(this.messageHtmlEditorOptions)
    }

    private submitMessage(): void {
        this.submitButton.option({ disabled: true })
        super.callEvent(this.options.onSubmit, {
            idLanguage: this.langSelectBox.option('value'),
            template: this.messageBox.option('value')
        })
    }

    private getAvailablelanguages(): Language[] {
        return this.systemIInternacinalizingService.getLanguages().filter((item: Language) => this.options.existedIdLanguages.indexOf(item.id) === -1)
    }

    //#endregion
}